import React, { useState } from 'react';

// US Citizenship Step 1: Welcome + Eligibility Screening
export const CitizenshipStep1EligibilityScreening = ({ formData, setFormData, onNext }) => {
  const [eligibilityPath, setEligibilityPath] = useState(formData.eligibilityPath || '');
  const [currentStatus, setCurrentStatus] = useState(formData.currentStatus || '');
  const [residenceDetails, setResidenceDetails] = useState(formData.residenceDetails || {});

  const eligibilityPaths = [
    {
      id: 'lawful-permanent-resident',
      title: 'Lawful Permanent Resident (Green Card Holder)',
      description: 'You have been a permanent resident for at least 5 years (or 3 years if married to US citizen)',
      icon: '💚',
      requirements: [
        'Green card for 5+ years (or 3+ if married to US citizen)',
        'Physical presence in US for at least half the time',
        'Continuous residence without trips over 6 months',
        'Good moral character'
      ]
    },
    {
      id: 'military-service',
      title: 'Military Service Member',
      description: 'Current or former US military service member with expedited eligibility',
      icon: '🎖️',
      requirements: [
        'Honorable service in US Armed Forces',
        'May apply while serving or after discharge',
        'Reduced residence requirements',
        'Special provisions for overseas service'
      ]
    },
    {
      id: 'spouse-us-citizen',
      title: 'Spouse of US Citizen',
      description: 'Married to US citizen with accelerated 3-year pathway',
      icon: '💑',
      requirements: [
        'Married to US citizen for 3+ years',
        'Green card for 3+ years',
        'Living with citizen spouse',
        'Spouse has been citizen for 3+ years'
      ]
    },
    {
      id: 'derivative-citizenship',
      title: 'Derivative Citizenship',
      description: 'Acquired citizenship through US citizen parent',
      icon: '👨‍👩‍👧‍👦',
      requirements: [
        'At least one US citizen parent',
        'Under 18 when parent naturalized (if after 2001)',
        'Green card holder at time of parent\'s naturalization',
        'Physical presence and residence requirements'
      ]
    },
    {
      id: 'other-pathway',
      title: 'Other Special Circumstances',
      description: 'Refugee, asylee, or other special immigration category',
      icon: '🛡️',
      requirements: [
        'Admitted as refugee or granted asylum',
        'Special immigrant status',
        'Other qualifying circumstances',
        'Meet specific pathway requirements'
      ]
    }
  ];

  const currentStatusOptions = [
    { value: 'green-card-holder', label: 'Green Card Holder (Permanent Resident)' },
    { value: 'conditional-resident', label: 'Conditional Permanent Resident' },
    { value: 'refugee', label: 'Refugee' },
    { value: 'asylee', label: 'Asylee' },
    { value: 'special-immigrant', label: 'Special Immigrant' },
    { value: 'military-member', label: 'Military Service Member' },
    { value: 'other', label: 'Other Status' }
  ];

  const handleEligibilitySelection = (pathId) => {
    setEligibilityPath(pathId);
    const selectedPath = eligibilityPaths.find(p => p.id === pathId);
    setFormData({
      ...formData,
      eligibilityPath: pathId,
      pathDetails: selectedPath
    });
  };

  const handleResidenceDetailChange = (field, value) => {
    const updated = { ...residenceDetails, [field]: value };
    setResidenceDetails(updated);
    setFormData({ ...formData, residenceDetails: updated });
  };

  const handleCurrentStatusChange = (status) => {
    setCurrentStatus(status);
    setFormData({ ...formData, currentStatus: status });
  };

  const handleContinue = () => {
    if (!eligibilityPath) {
      setEligibilityPath('lawful-permanent-resident'); // Default selection
      setFormData({
        ...formData,
        eligibilityPath: 'lawful-permanent-resident',
        pathDetails: eligibilityPaths.find(p => p.id === 'lawful-permanent-resident')
      });
    }
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">🇺🇸 Eligibility Screening for US Citizenship</h3>
        <p className="text-gray-400 text-lg">Let's determine your pathway to naturalization</p>
      </div>

      {/* Current Status */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">📋 Current Immigration Status</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2">Current Status</label>
            <select
              value={currentStatus}
              onChange={(e) => handleCurrentStatusChange(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="">Select your current status</option>
              {currentStatusOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-gray-300 mb-2">Date Status Obtained</label>
            <input
              type="date"
              value={residenceDetails.statusDate || ''}
              onChange={(e) => handleResidenceDetailChange('statusDate', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Eligibility Pathways */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">🛤️ Select Your Eligibility Pathway</h4>
        <div className="space-y-4">
          {eligibilityPaths.map((path) => (
            <div
              key={path.id}
              onClick={() => handleEligibilitySelection(path.id)}
              className={`p-6 rounded-lg border-2 transition-all cursor-pointer ${
                eligibilityPath === path.id
                  ? 'border-blue-500 bg-blue-900/20'
                  : 'border-gray-700 bg-gray-700 hover:border-blue-400'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center flex-1">
                  <div className={`w-6 h-6 rounded-full border-2 mr-4 flex items-center justify-center ${
                    eligibilityPath === path.id ? 'border-blue-500 bg-blue-500' : 'border-gray-500'
                  }`}>
                    {eligibilityPath === path.id && <span className="text-white text-sm">✓</span>}
                  </div>
                  <span className="text-3xl mr-4">{path.icon}</span>
                  <div className="flex-1">
                    <h5 className="text-xl font-semibold text-white">{path.title}</h5>
                    <p className="text-gray-300 mt-1">{path.description}</p>
                  </div>
                </div>
              </div>

              <div className="ml-10">
                <h6 className="text-blue-300 font-medium mb-2">Key Requirements:</h6>
                <ul className="space-y-1">
                  {path.requirements.map((req, index) => (
                    <li key={index} className="text-blue-200 text-sm flex items-center">
                      <span className="text-blue-400 mr-2">•</span>
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Basic Residence Information */}
      {eligibilityPath && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-white mb-4">🏠 Basic Residence Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 mb-2">Years in US as Permanent Resident</label>
              <select
                value={residenceDetails.yearsInUS || ''}
                onChange={(e) => handleResidenceDetailChange('yearsInUS', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
              >
                <option value="">Select years</option>
                <option value="less-than-3">Less than 3 years</option>
                <option value="3-4">3-4 years</option>
                <option value="5-plus">5+ years</option>
                <option value="10-plus">10+ years</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-300 mb-2">Current State of Residence</label>
              <input
                type="text"
                value={residenceDetails.currentState || ''}
                onChange={(e) => handleResidenceDetailChange('currentState', e.target.value)}
                placeholder="e.g., California, Texas, New York"
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      )}

      {/* Information Notice */}
      <div className="bg-blue-900/20 border border-blue-400 p-6 rounded-lg">
        <h4 className="text-blue-300 font-semibold mb-4">📋 Important Notes</h4>
        <ul className="text-blue-200 text-sm space-y-2">
          <li>• Most people need to be permanent residents for 5 years before applying</li>
          <li>• If married to a US citizen, you may qualify after just 3 years</li>
          <li>• Military members have special expedited pathways available</li>
          <li>• You must demonstrate continuous residence and physical presence in the US</li>
          <li>• Good moral character is required for naturalization</li>
        </ul>
      </div>

      {/* Navigation */}
      <div className="flex justify-end">
        <button
          onClick={handleContinue}
          className="px-6 py-3 rounded-lg font-medium transition-colors bg-blue-500 text-white hover:bg-blue-600"
        >
          Continue to Background Assessment →
        </button>
      </div>
    </div>
  );
};

// US Citizenship Step 2: Detailed Background Assessment
export const CitizenshipStep2BackgroundAssessment = ({ formData, setFormData, onNext, onBack }) => {
  const [backgroundDetails, setBackgroundDetails] = useState(formData.backgroundDetails || {});

  const handleBackgroundChange = (field, value) => {
    const updated = { ...backgroundDetails, [field]: value };
    setBackgroundDetails(updated);
    setFormData({ ...formData, backgroundDetails: updated });
  };

  const criminalHistoryOptions = [
    { value: 'none', label: 'No criminal history' },
    { value: 'minor-traffic', label: 'Minor traffic violations only' },
    { value: 'misdemeanor', label: 'Misdemeanor conviction(s)' },
    { value: 'felony', label: 'Felony conviction(s)' },
    { value: 'pending', label: 'Pending criminal charges' }
  ];

  const taxComplianceOptions = [
    { value: 'current', label: 'All taxes filed and current' },
    { value: 'minor-issues', label: 'Minor tax issues resolved' },
    { value: 'payment-plan', label: 'On payment plan with IRS' },
    { value: 'issues', label: 'Outstanding tax issues' },
    { value: 'never-filed', label: 'Have not filed required returns' }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">🔍 Detailed Background Assessment</h3>
        <p className="text-gray-400 text-lg">Comprehensive review of your background and moral character</p>
      </div>

      {/* Criminal History */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">⚖️ Criminal History</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-gray-300 mb-2">Criminal History Status</label>
            <select
              value={backgroundDetails.criminalHistory || ''}
              onChange={(e) => handleBackgroundChange('criminalHistory', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="">Select criminal history status</option>
              {criminalHistoryOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          {backgroundDetails.criminalHistory && backgroundDetails.criminalHistory !== 'none' && (
            <div>
              <label className="block text-gray-300 mb-2">Details of Criminal History</label>
              <textarea
                value={backgroundDetails.criminalDetails || ''}
                onChange={(e) => handleBackgroundChange('criminalDetails', e.target.value)}
                placeholder="Describe any arrests, charges, convictions, or pending cases. Include dates, charges, and outcomes."
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500 h-24"
              />
            </div>
          )}
        </div>
      </div>

      {/* Tax Compliance */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">💰 Tax Compliance</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-gray-300 mb-2">Tax Filing Status</label>
            <select
              value={backgroundDetails.taxCompliance || ''}
              onChange={(e) => handleBackgroundChange('taxCompliance', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="">Select tax compliance status</option>
              {taxComplianceOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 mb-2">Years Filed Taxes</label>
              <input
                type="text"
                value={backgroundDetails.yearsFiled || ''}
                onChange={(e) => handleBackgroundChange('yearsFiled', e.target.value)}
                placeholder="e.g., 2019-2023"
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2">Outstanding Tax Debt</label>
              <input
                type="text"
                value={backgroundDetails.taxDebt || ''}
                onChange={(e) => handleBackgroundChange('taxDebt', e.target.value)}
                placeholder="Enter amount owed (if any)"
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Good Moral Character */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">🎭 Good Moral Character Assessment</h4>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={backgroundDetails.habitualDrunkard || false}
                  onChange={(e) => handleBackgroundChange('habitualDrunkard', e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                />
                <span className="text-gray-300 text-sm">History of alcohol-related issues</span>
              </label>
            </div>
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={backgroundDetails.adulteryHistory || false}
                  onChange={(e) => handleBackgroundChange('adulteryHistory', e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                />
                <span className="text-gray-300 text-sm">History of adultery</span>
              </label>
            </div>
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={backgroundDetails.polygamy || false}
                  onChange={(e) => handleBackgroundChange('polygamy', e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                />
                <span className="text-gray-300 text-sm">Practice of polygamy</span>
              </label>
            </div>
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={backgroundDetails.illegalGambling || false}
                  onChange={(e) => handleBackgroundChange('illegalGambling', e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                />
                <span className="text-gray-300 text-sm">Illegal gambling</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Other Moral Character Issues</label>
            <textarea
              value={backgroundDetails.otherIssues || ''}
              onChange={(e) => handleBackgroundChange('otherIssues', e.target.value)}
              placeholder="Describe any other issues that might affect good moral character determination"
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500 h-20"
            />
          </div>
        </div>
      </div>

      {/* Military Service */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">🎖️ Military Service</h4>
        <div className="space-y-4">
          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={backgroundDetails.militaryService || false}
                onChange={(e) => handleBackgroundChange('militaryService', e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
              />
              <span className="text-gray-300">I have served in the U.S. Armed Forces</span>
            </label>
          </div>

          {backgroundDetails.militaryService && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-300 mb-2">Branch of Service</label>
                <select
                  value={backgroundDetails.militaryBranch || ''}
                  onChange={(e) => handleBackgroundChange('militaryBranch', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">Select branch</option>
                  <option value="army">Army</option>
                  <option value="navy">Navy</option>
                  <option value="air-force">Air Force</option>
                  <option value="marines">Marines</option>
                  <option value="coast-guard">Coast Guard</option>
                  <option value="space-force">Space Force</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-300 mb-2">Type of Discharge</label>
                <select
                  value={backgroundDetails.dischargeType || ''}
                  onChange={(e) => handleBackgroundChange('dischargeType', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">Select discharge type</option>
                  <option value="honorable">Honorable</option>
                  <option value="general">General (Under Honorable Conditions)</option>
                  <option value="other-than-honorable">Other Than Honorable</option>
                  <option value="bad-conduct">Bad Conduct</option>
                  <option value="dishonorable">Dishonorable</option>
                  <option value="still-serving">Still Serving</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Flexibility Notice */}
      <div className="bg-yellow-900/20 border border-yellow-400 p-4 rounded-lg">
        <h4 className="text-yellow-300 font-semibold mb-2">⚠️ Honesty is Critical</h4>
        <p className="text-yellow-200 text-sm">
          USCIS will conduct a thorough background check. It's better to disclose issues honestly than to hide them. Many issues can be overcome with proper explanation and documentation.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Eligibility Screening
        </button>
        <button
          onClick={onNext}
          className="px-6 py-3 rounded-lg font-medium transition-colors bg-blue-500 text-white hover:bg-blue-600"
        >
          Continue to Document Upload →
        </button>
      </div>
    </div>
  );
};

// US Citizenship Step 3: Document Collection & Upload
export const CitizenshipStep3DocumentUpload = ({ formData, setFormData, onNext, onBack }) => {
  const [documents, setDocuments] = useState(formData.documents || []);
  const [uploadCategory, setUploadCategory] = useState('identity-documents');

  const documentCategories = [
    {
      id: 'identity-documents',
      title: 'Identity & Status Documents',
      description: 'Primary identification and status documentation',
      icon: '🆔',
      examples: ['Green Card (I-551)', 'Passport', 'Driver\'s License', 'State ID', 'Birth Certificate']
    },
    {
      id: 'residence-evidence',
      title: 'Continuous Residence Evidence',
      description: 'Proof of continuous residence in the United States',
      icon: '🏠',
      examples: ['Tax returns', 'Bank statements', 'Employment records', 'Lease agreements', 'Utility bills']
    },
    {
      id: 'travel-documents',
      title: 'Travel & Absence Records',
      description: 'Documentation of trips outside the United States',
      icon: '✈️',
      examples: ['Passport stamps', 'I-94 records', 'Flight itineraries', 'Travel receipts', 'Work/vacation letters']
    },
    {
      id: 'criminal-records',
      title: 'Criminal History Records',
      description: 'Court records and criminal background documentation',
      icon: '⚖️',
      examples: ['Court records', 'Police reports', 'Disposition letters', 'Character references', 'Rehabilitation evidence']
    },
    {
      id: 'tax-records',
      title: 'Tax Documentation',
      description: 'Federal and state tax filing evidence',
      icon: '💰',
      examples: ['Tax returns (5 years)', 'W-2 forms', 'IRS transcripts', 'Payment records', 'Extension letters']
    },
    {
      id: 'marriage-family',
      title: 'Marriage & Family Documents',
      description: 'Marriage and family relationship evidence',
      icon: '💑',
      examples: ['Marriage certificate', 'Divorce decrees', 'Children\'s birth certificates', 'Spouse\'s citizenship proof']
    },
    {
      id: 'military-service',
      title: 'Military Service Records',
      description: 'Military service and discharge documentation',
      icon: '🎖️',
      examples: ['DD-214', 'Service records', 'Medical records', 'Awards and commendations', 'Character references']
    },
    {
      id: 'special-circumstances',
      title: 'Special Circumstances',
      description: 'Additional documents for special cases',
      icon: '📋',
      examples: ['Refugee documentation', 'Asylum records', 'Special immigrant evidence', 'Waiver applications']
    }
  ];

  const requiredDocumentsByPath = {
    'lawful-permanent-resident': [
      'Green Card (I-551)',
      'Tax returns (last 5 years)',
      'Passport',
      'Travel records',
      'Continuous residence evidence'
    ],
    'spouse-us-citizen': [
      'Green Card (I-551)',
      'Marriage certificate',
      'Spouse\'s citizenship proof',
      'Tax returns (last 3 years)',
      'Joint residence evidence'
    ],
    'military-service': [
      'Green Card (I-551)',
      'DD-214 discharge papers',
      'Military service records',
      'Tax returns',
      'Character references'
    ],
    'derivative-citizenship': [
      'Birth certificate',
      'Parent\'s citizenship certificate',
      'Green Card (I-551)',
      'Evidence of relationship',
      'Residence evidence'
    ]
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    const newDocuments = [];
    
    for (const file of files) {
      try {
        // Convert file to base64 for OCR analysis
        const fileData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        console.log(`🔍 Analyzing document: ${file.name} (${file.type}, ${file.size} bytes)`);

        // Call backend for actual document analysis with Google Vision OCR
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        const response = await fetch(`${backendUrl}/api/ai/analyze-uploaded-document`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            document_file: fileData,
            document_name: file.name,
            document_type: uploadCategory,
            case_context: {
              case_type: 'Citizenship Application',
              agent_type: 'citizenship',
              applicant: formData.personalInfo?.fullName || 'Citizenship Applicant',
              eligibility_path: formData.eligibilityPath || 'lawful-permanent-resident'
            }
          })
        });

        let documentAnalysis;
        if (response.ok) {
          documentAnalysis = await response.json();
          console.log(`✅ Document analysis successful for ${file.name}:`, documentAnalysis);
        } else {
          const errorText = await response.text();
          console.error(`❌ Document analysis failed for ${file.name}:`, response.status, errorText);
          documentAnalysis = {
            document_type: uploadCategory,
            content_description: `Document analysis failed: ${errorText}`,
            case_relevance: "Manual review required to determine relevance",
            exhibit_description: `Exhibit: ${file.name} requiring detailed manual review`,
            immigration_value: "Medium",
            analysis_error: `Analysis failed: ${errorText}`
          };
        }

        // Create document object with OCR analysis results
        const newDoc = {
          id: `doc_${Date.now()}_${Math.floor(performance.now() * 1000)}`,
          name: file.name,
          category: uploadCategory,
          file: file,
          timestamp: new Date().toISOString(),
          size: file.size,
          // Add OCR analysis results
          analysis: documentAnalysis,
          content_description: documentAnalysis.content_description,
          parties_mentioned: documentAnalysis.parties_mentioned,
          key_dates: documentAnalysis.key_dates,
          case_relevance: documentAnalysis.case_relevance,
          exhibit_description: documentAnalysis.exhibit_description,
          immigration_value: documentAnalysis.immigration_value,
          red_flags: documentAnalysis.red_flags,
          authenticity_indicators: documentAnalysis.authenticity_indicators,
          extracted_text: documentAnalysis.extracted_text
        };

        newDocuments.push(newDoc);
        
      } catch (error) {
        console.error('Document analysis error:', error);
        
        // Add document with error indication
        const newDoc = {
          id: `doc_${Date.now()}_${Math.floor(performance.now() * 1000)}`,
          name: file.name,
          category: uploadCategory,
          file: file,
          timestamp: new Date().toISOString(),
          size: file.size,
          analysis_error: "Analysis failed - manual review required",
          content_description: `Document analysis failed for ${file.name}`,
          case_relevance: "Manual review required",
          exhibit_description: `Exhibit: ${file.name} (manual review required)`,
          immigration_value: "Unknown"
        };
        
        newDocuments.push(newDoc);
      }
    }

    const updatedDocuments = [...documents, ...newDocuments];
    setDocuments(updatedDocuments);
    setFormData({ ...formData, documents: updatedDocuments });
  };

  const removeDocument = (docId) => {
    const updatedDocuments = documents.filter(doc => doc.id !== docId);
    setDocuments(updatedDocuments);
    setFormData({ ...formData, documents: updatedDocuments });
  };

  const getRequiredDocuments = () => {
    return requiredDocumentsByPath[formData.eligibilityPath] || [];
  };

  const getDocumentCompleteness = () => {
    const required = getRequiredDocuments();
    const uploaded = documents.map(doc => doc.name.toLowerCase());
    
    const found = required.filter(reqDoc => 
      uploaded.some(uploadedDoc => 
        uploadedDoc.includes(reqDoc.toLowerCase().replace(/\s+/g, '')) ||
        reqDoc.toLowerCase().replace(/\s+/g, '').includes(uploadedDoc.replace(/\s+/g, ''))
      )
    );

    return {
      found: found.length,
      total: required.length,
      percentage: required.length > 0 ? (found.length / required.length) * 100 : 0,
      missing: required.filter(reqDoc => 
        !uploaded.some(uploadedDoc => 
          uploadedDoc.includes(reqDoc.toLowerCase().replace(/\s+/g, '')) ||
          reqDoc.toLowerCase().replace(/\s+/g, '').includes(uploadedDoc.replace(/\s+/g, ''))
        )
      )
    };
  };

  const completeness = getDocumentCompleteness();

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">📁 Document Collection & Upload</h3>
        <p className="text-gray-400 text-lg">Gather and upload documents to support your naturalization application</p>
      </div>

      {/* Document Requirements */}
      <div className="bg-blue-900/20 border border-blue-400 p-6 rounded-lg">
        <h4 className="text-blue-300 font-semibold mb-4">📋 Required Documents for {formData.pathDetails?.title}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {getRequiredDocuments().map((doc, index) => (
            <div key={index} className="flex items-center text-blue-200 text-sm">
              <span className="text-blue-400 mr-2">•</span>
              {doc}
            </div>
          ))}
        </div>
      </div>

      {/* Document Category Selection */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">📂 Select Document Category</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {documentCategories.map((category) => (
            <div
              key={category.id}
              onClick={() => setUploadCategory(category.id)}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                uploadCategory === category.id
                  ? 'border-blue-500 bg-blue-900/20'
                  : 'border-gray-600 bg-gray-700 hover:border-blue-400'
              }`}
            >
              <div className="text-center">
                <div className="text-3xl mb-2">{category.icon}</div>
                <h5 className="text-white font-medium mb-1 text-sm">{category.title}</h5>
                <p className="text-gray-400 text-xs">{category.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* File Upload */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">📤 Upload Documents</h4>
        
        <div className="mb-4 p-4 bg-gray-700 rounded">
          <div className="flex items-center mb-2">
            <span className="text-2xl mr-3">
              {documentCategories.find(c => c.id === uploadCategory)?.icon}
            </span>
            <h5 className="text-white font-medium">
              {documentCategories.find(c => c.id === uploadCategory)?.title}
            </h5>
          </div>
          <p className="text-gray-300 text-sm mb-2">Examples:</p>
          <ul className="text-gray-400 text-sm space-y-1">
            {documentCategories.find(c => c.id === uploadCategory)?.examples.map((example, index) => (
              <li key={index}>• {example}</li>
            ))}
          </ul>
        </div>

        <div className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center">
          <input
            type="file"
            multiple
            onChange={handleFileUpload}
            className="hidden"
            id="file-upload"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
          />
          <label htmlFor="file-upload" className="cursor-pointer">
            <div className="text-6xl text-gray-500 mb-4">📁</div>
            <h4 className="text-xl text-white mb-2">Drop files here or click to upload</h4>
            <p className="text-gray-400 text-sm">
              Supported formats: PDF, Word, Images, Text files
            </p>
          </label>
        </div>
      </div>

      {/* Document Completeness */}
      {documents.length > 0 && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-xl font-semibold text-white">📋 Document Completeness</h4>
            <div className={`text-2xl font-bold ${
              completeness.percentage >= 80 ? 'text-emerald-400' :
              completeness.percentage >= 50 ? 'text-yellow-400' :
              'text-red-400'
            }`}>
              {Math.round(completeness.percentage)}%
            </div>
          </div>
          
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-300 mb-1">
              <span>Required Documents Found</span>
              <span>{completeness.found}/{completeness.total}</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  completeness.percentage >= 80 ? 'bg-emerald-400' :
                  completeness.percentage >= 50 ? 'bg-yellow-400' :
                  'bg-red-400'
                }`}
                style={{ width: `${completeness.percentage}%` }}
              ></div>
            </div>
          </div>

          {completeness.missing.length > 0 && (
            <div className="bg-yellow-900/20 border border-yellow-400 p-4 rounded">
              <h5 className="text-yellow-300 font-medium mb-2">Missing Documents:</h5>
              <ul className="text-yellow-200 text-sm space-y-1">
                {completeness.missing.map((doc, index) => (
                  <li key={index}>• {doc}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Uploaded Documents */}
      {documents.length > 0 && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-white mb-4">📋 Uploaded Documents ({documents.length})</h4>
          <div className="space-y-3">
            {documents.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-4 bg-gray-700 rounded">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">
                    {documentCategories.find(c => c.id === doc.category)?.icon || '📄'}
                  </span>
                  <div>
                    <h5 className="text-white font-medium">{doc.name}</h5>
                    <p className="text-gray-400 text-sm">
                      {documentCategories.find(c => c.id === doc.category)?.title} • 
                      {(doc.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => removeDocument(doc.id)}
                  className="text-red-400 hover:text-red-300 p-2"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Flexibility Notice */}
      <div className="bg-emerald-900/20 border border-emerald-400 p-4 rounded-lg">
        <h4 className="text-emerald-300 font-semibold mb-2">✅ Flexible Process</h4>
        <p className="text-emerald-200 text-sm">
          You can proceed to the next step even without uploading all documents now. You can always return to add more documents later in the process.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Background Assessment
        </button>
        <button
          onClick={onNext}
          className="px-6 py-3 rounded-lg font-medium transition-colors bg-blue-500 text-white hover:bg-blue-600"
        >
          Continue to Travel History →
        </button>
      </div>
    </div>
  );
};